import {
  Country,
  ErrorCode,
  FirstPaymentType,
  CheckoutPaymentSchedule,
  Promotion,
  CardDetails,
  Cohort,
  OrderSource,
  Item,
  FacilityApplicationIntroductionEligibility,
} from '@afterpaytouch/portal-api/types'
import { parseUrl, stringifyUrl } from 'query-string'
import { TopazCardErrorReason } from './topaz'
import { TopazError } from './errors'
import { OAuthParams } from './oAuth'
import { toOAuthResponseType } from '../utils/oAuth'
import { BankAccountDetails, CardBrand, Money, PaymentCardDetails, PaymentMethodType, PaymentType } from '@afterpay/types'
import { ParsedUrlQuery } from 'querystring'
import { MessageKinds, Theme } from '@afterpaytouch/core'
import { app } from '../env'
import { FetchVerificationErrorResponse } from '@afterpaytouch/event-verify'
import { GenericApiError } from './http'

// TODO improve the skipPrefill by using this skipPrefill value in the state CHECKOUT-3939
export enum AllowedQueryParam {
  hideHeader = 'hideHeader',
  skipPrefill = 'skipPrefill',
}

export interface AllowedQuery {
  hideHeader: boolean
  skipPrefill: boolean
}

export enum PreferredPaymentMethodStatus {
  VALID,
  INVALID_BRAND_SWITCHED,
  INVALID_BRAND_CANNOT_SWITCH,
  EXPIRING_SWITCHED,
  EXPIRING_CANNOT_SWITCH,
}

export enum PaymentInterval {
  MONTHLY,
  FORTNIGHTLY,
}

export const paymentIntervalUnit: Partial<Record<PaymentInterval, string>> = {
  [PaymentInterval.FORTNIGHTLY]: 'day',
  [PaymentInterval.MONTHLY]: 'month',
}

// Should match localization keys in summary.json
export type CardErrorReason =
  | 'invalidBrand'
  | 'cardExpiry'
  | 'achNotAllowed'
  | 'creditCardNotAllowed'
  | 'cardExpiryLimitInMonths'
  | 'cashAppCardIneligible'
  | 'dssProhibitedPaymentMethod'
  | 'fallbackGenericMessage'
  | TopazCardErrorReason

export type BankAccountErrorReason = 'unLinked' | 'noValidCard' | 'checkingNotAllowed'

export interface PaymentMethod {
  paymentMethodType?: PaymentMethodType
}

export interface PaymentOption {
  id: number
  totalAmount: Money
  amountDueToday: Money
  interestAmount: null | Money
  apr: null | number
  installmentsCount: number
  installmentAmount: Money
  paymentInterval: 'FORTNIGHTLY' | 'MONTHLY'
  paymentType: PaymentType
  principalAmount: Money
  repaymentAmount: Money | null
  maxLoanAmount: Money | null
  rank?: number
  paymentSchedules?: CheckoutPaymentSchedule[]
}
export interface NotificationError {
  severe: boolean
  heading?: string
  message: React.ReactNode
  kind?: MessageKinds
}

export interface EnrichedCardDetails extends CardDetails {
  paymentMethodType: PaymentMethodType.CREDIT_CARD
  error: CardErrorReason | null
}

export interface EnrichedBankAccountDetails extends BankAccountDetails {
  paymentMethodType: PaymentMethodType.ACH_BANK_ACCOUNT
  error: BankAccountErrorReason | null
}

export interface ApplePayDetails {
  paymentMethodType: PaymentMethodType.APPLE_PAY
  token: string
}

export const lastPaymentDate = (paymentSchedules: CheckoutPaymentSchedule[]): Date => {
  if (paymentSchedules.length === 0) {
    return new Date()
  }

  const dueDate = paymentSchedules[paymentSchedules.length - 1].installmentDueDate
  return dueDate === null ? new Date() : new Date(dueDate)
}

export interface StartedAmountData {
  paymentSchedules: CheckoutPaymentSchedule[]
  amountDueToday: Money
  paymentUpFront: boolean
  consumerAmount: Money
  merchantAmount: Money
  installmentAmount: Money
  downPaymentAmount: Money
  downPaymentType: FirstPaymentType
}
export interface RetrieveCardsRequest {
  profilingSessionId: string
}

export interface RetrieveCardsResponse {
  savedCards: CardDetails[]
  traceId: string
}

export interface ConfirmedCheckout {
  smsEnabled: boolean
  status: 'SUCCESS' | 'FAILURE' | 'CANCELLED'
  callbackBaseUrl: string
  callbackUrlQueryArgs: string
  declined: boolean // Where do we use this boolean?
}

export interface ConfirmCheckoutData {
  paymentMethodId?: number
  paymentMethodType?: PaymentMethodType
  donation?: string
  autopayEnabled?: boolean
  creditCheckAgree: boolean
  paymentType?: PaymentType
  shouldUpdatePreferredPaymentDay?: boolean
  accountOpeningDisclosureAgree?: boolean
  checkoutCohort?: Cohort
  checkoutTheme?: Theme
}

export interface ConfirmCheckoutRequest extends ConfirmCheckoutData {
  profilingSessionId: string
  checkoutClient: string
}

export interface ConfirmCheckoutRequestParams {
  shouldUpdatePreferredPaymentDay?: boolean
}

export interface UpdatedCheckout {
  paymentSchedules: CheckoutPaymentSchedule[]
  amountDueToday: Money
  paymentUpFront: boolean
  consumerAmount: Money
  merchantAmount: Money
  exchangeRate: string
  installmentAmount: Money
  downPaymentAmount: Money
  downPaymentType: FirstPaymentType
  promotion?: Promotion
  purchasePaymentAgreementDocumentUrl?: string
  paymentOption?: PaymentOption | null
  paymentOptions?: PaymentOption[] | null
}

export interface UpdateCheckoutData {
  amount: Money
  shippingAddressId?: number | string
  shippingOptionIdentifier?: string
  isPickUp?: boolean
}

export interface UpdateCheckoutRequest extends UpdateCheckoutData {
  profilingSessionId: string
}

export interface VerifyEFARobocallEventRequest {
  onSuccess: () => void
  onError: (d: FetchVerificationErrorResponse) => void
}

export const shippingOptionKeys = ['id', 'name', 'description', 'shippingAmount', 'taxAmount', 'orderAmount']

export type EntryPoint = 'product-page' | 'mini-cart' | 'cart' | null
const toEntryPoint = (str: string): EntryPoint => {
  switch (str) {
    case 'product-page':
    case 'mini-cart':
    case 'cart':
      return str
    default:
      return null
  }
}
export type MessageProtocol = 'json' | 'zoid' | 'legacy'
const toMessageProtocol = (str: string): MessageProtocol => {
  switch (str) {
    case 'json':
    case 'zoid':
    case 'legacy':
      return str
    default:
      return 'legacy'
  }
}

export const AddressModeType = {
  ADDRESS_WITH_SHIPPING_OPTIONS: 'ADDRESS_WITH_SHIPPING_OPTIONS',
  ADDRESS_WITHOUT_SHIPPING_OPTIONS: 'ADDRESS_WITHOUT_SHIPPING_OPTIONS',
  SHIP_TO_ORDER_ADDRESS: 'SHIP_TO_ORDER_ADDRESS',
  PICKUP_FROM_ORDER_ADDRESS: 'PICKUP_FROM_ORDER_ADDRESS',
  NO_ADDRESS: 'NO_ADDRESS',
  SHIP_TO_ORDER_ADDRESS_WITHOUT_SHIPPING_OPTIONS: 'SHIP_TO_ORDER_ADDRESS_WITHOUT_SHIPPING_OPTIONS',
} as const

export type AddressMode = keyof typeof AddressModeType

// Undefined type is only used to satisfy TS complaining about using `.includes` with `undefined`
export const nonShippingInfoAddressModes: Array<AddressMode | undefined> = [
  AddressModeType.NO_ADDRESS,
  AddressModeType.SHIP_TO_ORDER_ADDRESS_WITHOUT_SHIPPING_OPTIONS,
]

export enum Brand {
  AFTERPAY = 'afterpay',
  CASHAPP = 'cashapp',
}

export enum EStatus {
  ELIGIBLE = 'eligible',
  VISITOR_NOT_ELIGIBLE = 'visitor-not-eligible',
  MERCHANT_NOT_ELIGIBLE = 'merchant-not-eligible',
}

interface CheckoutParams {
  readonly token: string
  readonly relativeCallbackUrl: string
  readonly isWindowed: boolean
  readonly isIframe: boolean
  readonly zoid: string
  readonly protocol?: MessageProtocol
  readonly buyNow: boolean
  readonly pickup: boolean
  readonly shippingOptionRequired: boolean
  readonly entryPoint: EntryPoint
  readonly checkoutButtonLabel: string
  readonly visitorId?: string
  readonly addressMode?: AddressMode
  readonly isAfterpayInAppSession: boolean
  readonly brand?: string
  readonly e?: string
  readonly g?: string
  readonly eStatus?: string
  readonly deviceId?: string
}

export type InitialData = CheckoutParams & OAuthParams

export const parseQueryParamValue = (value: string | string[] | undefined): boolean => {
  // NOTE: This doesn't support string arrays for "value" argument
  const validValueList = ['', 'true']
  const isValidValue = typeof value === 'string' && validValueList.includes(value)
  return isValidValue
}

export const parseUrlQueryToState = (query: ParsedUrlQuery): AllowedQuery => {
  const allowedQueryParam = Object.values(AllowedQueryParam)
  const initialState = {}

  allowedQueryParam.forEach((name) => {
    initialState[name] = parseQueryParamValue(query[name])
  })

  return initialState as AllowedQuery
}

export const parseQueryStateToUrl = (allowedQuery: AllowedQuery | undefined): string => {
  if (typeof allowedQuery === 'undefined') {
    return ''
  }
  const allowedQueryParam = Object.values(AllowedQueryParam)
  return allowedQueryParam.filter((name) => allowedQuery[name]).join('&')
}

export function parseInitialData(query: ParsedUrlQuery): InitialData | null {
  function get(field: string, fallback: null): string | null
  function get(field: string, fallback?: string): string
  function get(field: string, fallback: string | null = ''): string | null {
    const item = query[field]
    if (typeof item === 'string') {
      return item
    }
    if (Array.isArray(item)) {
      return item[0] ?? ''
    }
    return fallback
  }
  const bool = (field: string, fallback: boolean = false): boolean => {
    const value = get(field)
    return value === 'true' || (value !== 'false' && fallback)
  }
  const str = (field: string, fallback: string = ''): string => {
    return get(field, fallback)
  }
  const token = get('token')
  if (token === '') {
    return null
  }
  const protocol = {
    protocol: toMessageProtocol(get('protocol')),
  }
  const isWindowed = bool('isWindowed')
  const isIframe = bool('isIframe')

  const brand = str('brand')
  const e = str('e')
  const g = str('g')
  const eStatus = str('eStatus')
  const deviceId = str('device_id')

  const overwriteExpressConfig = (): Pick<CheckoutParams, 'buyNow' | 'pickup' | 'shippingOptionRequired' | 'addressMode'> => {
    const modeMap = {
      ADDRESS_WITH_SHIPPING_OPTIONS: {
        shippingOptionRequired: true,
        pickup: false,
        buyNow: true,
        addressMode: AddressModeType.ADDRESS_WITH_SHIPPING_OPTIONS,
      },
      ADDRESS_WITHOUT_SHIPPING_OPTIONS: {
        shippingOptionRequired: false,
        pickup: false,
        buyNow: false,
        addressMode: AddressModeType.ADDRESS_WITHOUT_SHIPPING_OPTIONS,
      },
      SHIP_TO_ORDER_ADDRESS: {
        shippingOptionRequired: true,
        pickup: false,
        buyNow: true,
        addressMode: AddressModeType.SHIP_TO_ORDER_ADDRESS,
      },
      SHIP_TO_ORDER_ADDRESS_WITHOUT_SHIPPING_OPTIONS: {
        shippingOptionRequired: false,
        pickup: false,
        buyNow: true,
        addressMode: AddressModeType.SHIP_TO_ORDER_ADDRESS_WITHOUT_SHIPPING_OPTIONS,
      },
      PICKUP_FROM_ORDER_ADDRESS: {
        shippingOptionRequired: true,
        pickup: true,
        buyNow: true,
        addressMode: AddressModeType.PICKUP_FROM_ORDER_ADDRESS,
      },
      NO_ADDRESS: {
        shippingOptionRequired: false,
        pickup: false,
        buyNow: true,
        addressMode: AddressModeType.NO_ADDRESS,
      },
    }

    const matchAddressModeFromQuery = (fields: [boolean, boolean, boolean]): AddressMode => {
      const fieldMap = {
        'true,false,true': AddressModeType.ADDRESS_WITH_SHIPPING_OPTIONS,
        'true,false,false': AddressModeType.ADDRESS_WITH_SHIPPING_OPTIONS, // compatibility for the original integrated shipping
        'false,false,false': AddressModeType.ADDRESS_WITHOUT_SHIPPING_OPTIONS,
        'true,true,true': AddressModeType.PICKUP_FROM_ORDER_ADDRESS,
        'false,false,true': AddressModeType.NO_ADDRESS,
      }

      return fieldMap[fields.toString()] ?? AddressModeType.ADDRESS_WITH_SHIPPING_OPTIONS
    }

    const addressMode = query.addressMode as string | null
    if (addressMode !== null && addressMode !== '' && Object.keys(modeMap).includes(addressMode)) {
      return modeMap[addressMode]
    }

    if (['buyNow', 'pickup', 'shippingOptionRequired'].every((field) => typeof query[field] === 'undefined')) {
      return modeMap.ADDRESS_WITH_SHIPPING_OPTIONS
    }

    const buyNow = bool('buyNow')
    const pickup = bool('pickup')
    const shippingOptionRequired = bool('shippingOptionRequired', true)

    return {
      buyNow,
      pickup,
      shippingOptionRequired,
      addressMode: matchAddressModeFromQuery([shippingOptionRequired, pickup, buyNow]),
    }
  }

  return {
    token,
    relativeCallbackUrl: get('relativeCallbackUrl'),
    isWindowed,
    isIframe,
    brand,
    e,
    g,
    eStatus,
    deviceId,
    zoid: get('zoid'),
    entryPoint: toEntryPoint(get('entryPoint')),
    checkoutButtonLabel: get('checkoutButtonLabel'),
    isAfterpayInAppSession: bool('isAfterpayInAppSession'),
    ...overwriteExpressConfig(),
    ...(get('client_id', null) !== null && { clientId: get('client_id', null)! }),
    ...(toOAuthResponseType(get('response_type', null)) !== null && { responseType: toOAuthResponseType(get('response_type', null))! }),
    ...(get('redirect_uri', null) !== null && { redirectUri: get('redirect_uri', null)! }),
    ...(get('scope', null) !== null && { scope: get('scope', null)! }),
    ...(get('state', null) !== null && { state: get('state', null)! }),
    ...(get('visitor_id', null) !== null && { visitorId: get('visitor_id', null)! }),
    ...((isIframe || isWindowed) && protocol),
  }
}

export const applyInitialData = (inputUrl: string, data: InitialData): string => {
  const { url, query } = parseUrl(inputUrl)
  return stringifyUrl({
    url,
    query: { ...data, ...query },
  })
}

export enum CheckoutStatus {
  Initial,
  Loading,
  Loaded,
  Error,
  IdentityIneligible,
  Starting,
  LackOfEligiblePaymentMethod,
  Started,
  FirstPaymentMethodRequired,
  SetupPaymentMethodError,
  Updating,
  UpdateError,
  Confirming,
  Confirmed,
  Closed,
}

export const iabFlowCheckoutLoadingStates = [CheckoutStatus.Starting]

export enum NotificationStatus {
  Unread,
  Read,
}

export enum PaymentOptionsStatus {
  Initial,
  Error,
  Fetching,
  Fetched,
  Updating,
  UpdateError,
}

export interface OAuthData {
  client_id: string
  response_type: string
  redirect_uri: string
  scope: string
  state: string
}

export interface CrossRegionUserDetails {
  consumerEmail: string
  consumerCountryCode: Country
  consumerMobile?: string
}

export interface CrossRegionRedirection {
  crossRegionCheckoutUrl: string
}

export interface UpdatedPaymentOption {
  paymentSchedules: CheckoutPaymentSchedule[]
  paymentOption: PaymentOption
  savedCards: CardDetails[]
  preferredCard: CardDetails | null
  savedBankAccounts: BankAccountDetails[]
  preferredBankAccount: BankAccountDetails | null
  promotion?: Promotion
  traceId: string
  purchasePaymentAgreementDocumentId?: string
  maximumOrderValue?: Money
  paymentRoutingKey?: string
}

export interface CreateDocumentsRequest {
  autopay: boolean
}

export enum OrderTransactionDocumentType {
  INSTALLMENT_AGREEMENT = 'INSTALLMENT_AGREEMENT',
  FINAL_PAYMENT_SCHEDULE = 'FINAL_PAYMENT_SCHEDULE',
  LOAN_AGREEMENT = 'LOAN_AGREEMENT',
  PAYMENT_AUTHORISATION = 'PAYMENT_AUTHORISATION',
  CREDIT_SCORE_DISCLOSURE = 'CREDIT_SCORE_DISCLOSURE',
}

// Strings should match the localisations in `terminalError`
export enum TerminalErrorCode {
  Default = 'default',
  AuthenticationDeclined = 'authenticationDeclined',
  PasswordResetDeclined = 'passwordResetDeclined',
  AccountClosed = 'accountClosed',
  AccountDisabled = 'accountDisabled',
  AccountFrozen = 'accountFrozen',
  AmountTooHigh = 'amountTooHigh',
  AccountPendingDeletion = 'accountPendingDeletion',
  ChargebackPending = 'chargebackPending',
  DuplicateIdentity = 'duplicateIdentity',
  HighRiskNationality = 'highRiskNationality',
  DuplicatePaymentCardError = 'duplicatePaymentCardError',
  IneligibleState = 'ineligibleState',
  InsufficientFunds = 'insufficientFunds',
  MaxActiveOrdersReached = 'maxActiveOrdersReached',
  MaxActiveOrdersReachedDetails = 'maxActiveOrdersReached_details',
  MaxAttempts = 'resetCodeMaxAttempts',
  MaxAttemptsForType = 'identityCheckMaxAttempts',
  MaxDobVerificationAttempts = 'maxDobVerificationAttempts',
  IdentityDeclined = 'identityDeclined',
  InvalidPreorder = 'invalidPreorder',
  UnsupportedOrder = 'unsupportedOrder',
  MerchantOrderAmountLimitExceeded = 'merchantOrderAmountLimitExceeded',
  MinimumNotMet = 'minimumNotMet',
  OrderAmountTooHigh = 'orderAmountTooHigh',
  OverduePayments = 'overdue',
  OverduePaymentsCheckoutPaymentEligible = 'overduePaymentEligible',
  RiskDeclined = 'riskDeclined',
  ExcessiveLateFeeAmount = 'excessiveLateFeeAmount',
  ExcessiveLateFeeNumber = 'excessiveLateFeeNumber',
  UnsupportedMerchantTradingCountry = 'unsupportedMerchantTradingCountry',
  InvalidPickupAddress = 'invalidPickupAddress',
  UnableToUpdateCheckout = 'unableToUpdateCheckout',
  InvalidAmount = 'invalidAmount',
  StateRestrictionsPCL = 'stateRestrictionsPCL',
  GiftCardIneligible = 'giftCardIneligible',
  MerchantLimitZero = 'merchantLimitZero',
  IdentityVerificationFailedManual = 'identityVerificationFailedManual',
  IdentityVerificationTimeout = 'identityVerificationTimeout',
  OrderAmountExceedsSpendCap = 'orderAmountExceedsSpendCap',

  // Payment Options related terminal error codes
  ErrorPCL = 'errorPCL',
  RiskDeclinedPCL = 'riskDeclinedPCL',
  AmountTooHighDeclinePCL = 'amountTooHighDeclinePCL',
  MaxActiveOrdersReachedDeclinePCL = 'maxActiveOrdersReachedDeclinePCL',
  CreditFreezeDeclinedPCL = 'creditFreezeDeclinedPCL',
  ExtendedFraudAlert = 'extendedFraudAlert',

  // IDV for PCL and AFF related terminal error codes
  IdentityVerificationDeclinedPCL = 'identityVerificationDeclinedPCL',
  PaywatchCheckFailed = 'paywatchCheckFailed',
  BlockSignUpFromFRITES = 'blockSignUpFromFRITES',
  BiometricCheckMaxRetriesReached = 'BiometricCheckMaxRetriesReached',
  IdentityVerificationMaxAttemptsAFF = 'identityVerificationMaxAttemptsAFF',

  // topaz error
  ExceededMaxAttemptsEnteringDetails = 'exceededMaxAttemptsEnteringDetails',

  // uk credit check
  ComplianceDeclineBureauNoHit = 'complianceDeclineBureauNoHit',
  ComplianceDeclineBureau = 'complianceDeclineBureau',

  // nz credit check
  CreditCheckFailure = 'creditCheckFailure',
  LimitInternalError = 'limitInternalError',

  PasswordResetNotAllowed = 'PasswordResetNotAllowed',
  KYCRefreshPending = 'KYCRefreshPending',
  ShadowAccountIneligible = 'shadowAccountIneligible',
  UnsuitabilityDecline = 'unsuitabilityDecline',
}

export const isTerminalErrorCode = (e: unknown): e is TerminalErrorCode => Object.values(TerminalErrorCode).includes(e as TerminalErrorCode)

// Values should match the localisations in `summary.error` and `paymentMethod.error`
export enum RecoverableErrorCode {
  UnsupportedCardIssuerBank = 'unsupportedCardIssuerBank',
  UnsupportedCardIssuer = 'foreignCard',
  UnsupportedCardType = 'prepaidCard',
  AddPaymentActiveCardLimitExceeded = 'activeCardLimitExceeded',
  AddPaymentInactiveCardLimitExceeded = 'inactiveCardLimitExceeded',
  CreditCardProhibitedForPaymentType = 'creditCardProhibitedForPaymentType',
  CashAppCardIneligible = 'cashAppCardIneligible',
}

export interface PaymentOptionsError {
  error: TerminalErrorCode
  errorCode?: ErrorCode
  extra?: {
    maximumOrderValue?: string
    contactNumber?: string
    creditFreezeApplicationDeadline?: string
    tooManyRequests?: boolean
  }
}

export enum SessionIdVisibility {
  HIDDEN,
  VISIBLE,
}

export interface SessionIdState {
  visibility: SessionIdVisibility
}

export interface CheckoutError {
  error?: TerminalErrorCode | RecoverableErrorCode | string
  errorCode?: ErrorCode | TopazError | TerminalErrorCode | 'Hard_ID_Max_Failures'
  extra?: {
    availableToSpend?: Money
    minimumOrderValue?: Money
    maximumOrderValue?: string
    numberOrdersToPay?: string
    numberActiveOrders?: string
    payMonthlyOnly?: boolean
    contactNumber?: string
    isEFAFinished?: boolean
    facilityApplicationIntroductionEligibility?: FacilityApplicationIntroductionEligibility
  }
  showBackToStore?: boolean
}

export interface CheckoutPreferredCardsAndBankAccounts {
  savedBankAccounts: BankAccountDetails[]
  savedCards: PaymentCardDetails[]
  enabledCardTypes: CardBrand[]
}

export const checkoutErrors: Partial<Record<ErrorCode | TopazError, TerminalErrorCode | RecoverableErrorCode>> = {
  [ErrorCode.AmountTooHigh]: TerminalErrorCode.AmountTooHigh,
  [ErrorCode.ChargebackPending]: TerminalErrorCode.ChargebackPending,
  [ErrorCode.OrderAmountExceedsSpendCap]: TerminalErrorCode.OrderAmountExceedsSpendCap,
  [ErrorCode.OrderAmountTooHigh]: TerminalErrorCode.OrderAmountTooHigh,
  [ErrorCode.AccountClosed]: TerminalErrorCode.AccountClosed,
  [ErrorCode.AccountDisabled]: TerminalErrorCode.AccountDisabled,
  [ErrorCode.AccountFrozen]: TerminalErrorCode.AccountFrozen,
  [ErrorCode.AccountPendingDeletion]: TerminalErrorCode.AccountPendingDeletion,
  [ErrorCode.OverduePayments]: TerminalErrorCode.OverduePayments,
  // in beta we treat OverduePaymentsCheckoutPaymentEligible error same as OverduePayments
  [ErrorCode.OverduePaymentsCheckoutPaymentEligible]: TerminalErrorCode.OverduePaymentsCheckoutPaymentEligible,
  [ErrorCode.IneligibleState]: TerminalErrorCode.IneligibleState,
  [ErrorCode.MinimumNotMet]: TerminalErrorCode.MinimumNotMet,
  [ErrorCode.Declined]: TerminalErrorCode.RiskDeclined,
  [ErrorCode.ProbationLimitExceeded]: TerminalErrorCode.RiskDeclined,
  [ErrorCode.RiskDeclineUnclassified]: TerminalErrorCode.RiskDeclined,
  [ErrorCode.InvalidPreorder]: TerminalErrorCode.InvalidPreorder,
  [ErrorCode.UnsupportedOrder]: TerminalErrorCode.UnsupportedOrder,
  [ErrorCode.ExcessiveLateFeeAmount]: TerminalErrorCode.ExcessiveLateFeeAmount,
  [ErrorCode.ExcessiveLateFeeNumber]: TerminalErrorCode.ExcessiveLateFeeNumber,
  [ErrorCode.UnableToProcessOrder]: TerminalErrorCode.RiskDeclined,
  [ErrorCode.MerchantOrderAmountLimitExceeded]: TerminalErrorCode.MerchantOrderAmountLimitExceeded,
  [ErrorCode.MaxActiveOrdersReached]: TerminalErrorCode.MaxActiveOrdersReached,
  [ErrorCode.UnsupportedCardIssuerBank]: RecoverableErrorCode.UnsupportedCardIssuerBank,
  [ErrorCode.UnsupportedCardIssuer]: RecoverableErrorCode.UnsupportedCardIssuer,
  [ErrorCode.UnsupportedCardType]: RecoverableErrorCode.UnsupportedCardType,
  [ErrorCode.UnsupportedMerchantTradingCountry]: TerminalErrorCode.UnsupportedMerchantTradingCountry,
  [ErrorCode.InvalidPickupAddress]: TerminalErrorCode.InvalidPickupAddress,
  [ErrorCode.CashAppCardIneligible]: RecoverableErrorCode.CashAppCardIneligible,
  [ErrorCode.UnableToUpdateCheckout]: TerminalErrorCode.UnableToUpdateCheckout,
  [ErrorCode.CreditCardProhibitedForPaymentType]: RecoverableErrorCode.CreditCardProhibitedForPaymentType,
  [ErrorCode.InvalidAmount]: TerminalErrorCode.InvalidAmount,
  [ErrorCode.RiskDeclined]: TerminalErrorCode.RiskDeclined,
  [ErrorCode.GiftCardIneligible]: TerminalErrorCode.GiftCardIneligible,
  [ErrorCode.MerchantLimitZero]: TerminalErrorCode.MerchantLimitZero,
  [ErrorCode.LoanCapExceeded]: TerminalErrorCode.RiskDeclined,
  // below are id check related
  [ErrorCode.IdentityCheckGatewayError]: TerminalErrorCode.IdentityDeclined,
  [ErrorCode.IdentityCheckRejected]: TerminalErrorCode.IdentityDeclined,
  [ErrorCode.IncompleteConsumerProfile]: TerminalErrorCode.Default,
  [ErrorCode.VerifiedMobileMissing]: TerminalErrorCode.IdentityDeclined,
  [ErrorCode.MaxAttemptsReached]: TerminalErrorCode.IdentityDeclined,
  [ErrorCode.MaxAttemptsReachedForType]: TerminalErrorCode.IdentityDeclined,
  [ErrorCode.NotFound]: TerminalErrorCode.Default,
  [ErrorCode.UnableToProcessOrderTemporarily]: TerminalErrorCode.Default,
  [ErrorCode.StateRestrictionsPCL]: TerminalErrorCode.StateRestrictionsPCL,
  [ErrorCode.IdentityVerificationDeclinedPCL]: TerminalErrorCode.IdentityVerificationDeclinedPCL,
  [ErrorCode.PaywatchCheckFailed]: TerminalErrorCode.PaywatchCheckFailed,
  [ErrorCode.ShadowAccountIneligible]: TerminalErrorCode.ShadowAccountIneligible,

  // Payment method issues
  [ErrorCode.AddPaymentActiveCardLimitExceeded]: RecoverableErrorCode.AddPaymentActiveCardLimitExceeded,
  [ErrorCode.AddPaymentInactiveCardLimitExceeded]: RecoverableErrorCode.AddPaymentInactiveCardLimitExceeded,

  // Topaz related issues
  [TopazError.DuplicatePayCardError]: TerminalErrorCode.DuplicatePaymentCardError,
  [TopazError.ExceededMaxAttemptsEnteringDetails]: TerminalErrorCode.ExceededMaxAttemptsEnteringDetails,

  // uk credit check
  [ErrorCode.ComplianceDeclineBureauNoHit]: TerminalErrorCode.ComplianceDeclineBureauNoHit,
  [ErrorCode.ComplianceDeclineBureau]: TerminalErrorCode.ComplianceDeclineBureau,

  // nz credit check
  [ErrorCode.CreditCheckFailure]: TerminalErrorCode.CreditCheckFailure,
  [ErrorCode.LimitInternalError]: TerminalErrorCode.LimitInternalError,

  // reg au check
  [ErrorCode.UnsuitabilityDecline]: TerminalErrorCode.UnsuitabilityDecline,

  [ErrorCode.ProfileRefreshOverduePending]: TerminalErrorCode.KYCRefreshPending,
  // generic error
  [ErrorCode.Error]: TerminalErrorCode.Default,
}

export const paymentOptionsErrors: Partial<Record<ErrorCode, TerminalErrorCode>> = {
  // Payment Options related issues
  [ErrorCode.Error]: TerminalErrorCode.ErrorPCL,
  [ErrorCode.RiskDeclined]: TerminalErrorCode.RiskDeclinedPCL,
  [ErrorCode.AmountTooHigh]: TerminalErrorCode.AmountTooHighDeclinePCL,
  [ErrorCode.MaxActiveOrdersReached]: TerminalErrorCode.MaxActiveOrdersReachedDeclinePCL,
  [ErrorCode.ExcessiveLateFeeAmount]: TerminalErrorCode.ExcessiveLateFeeAmount,
  [ErrorCode.ExtendedFraudAlert]: TerminalErrorCode.ExtendedFraudAlert,
}

export const nonTerminalErrors: ErrorCode[] = [
  ErrorCode.UnsupportedCardIssuerBank,
  ErrorCode.UnsupportedCardIssuer,
  ErrorCode.UnsupportedCardType,
  ErrorCode.CashAppCardIneligible,
  ErrorCode.CardScanRequired,
  ErrorCode.Unauthorized,
  ErrorCode.CreditCardProhibitedForPaymentType,
  ErrorCode.CheckoutConfirm2FARequired,
  ErrorCode.CheckoutConfirm2FARequiredNoEmail,
  ErrorCode.InstallmentAmountsMismatch,
  ErrorCode.ComplianceDeclineSystemError,
  ErrorCode.CardNotEnabled,
  ErrorCode.ExtendedFraudAlert,
]
export const otbWhitelist: ErrorCode[] = [ErrorCode.OrderAmountTooHigh, ErrorCode.AmountTooHigh]

export const getCheckoutIdentityError = (errorCode: string): CheckoutError => {
  let errorCheckout: TerminalErrorCode | RecoverableErrorCode | string
  let errorCodeCheckout: ErrorCode | TopazError | 'Hard_ID_Max_Failures' | string
  switch (errorCode) {
    case ErrorCode.HighRiskNationality:
      errorCheckout = TerminalErrorCode.HighRiskNationality
      errorCodeCheckout = ErrorCode.HighRiskNationality
      break
    default:
      errorCheckout = checkoutErrors[errorCode as ErrorCode] ?? TerminalErrorCode.Default
      errorCodeCheckout = errorCode
  }

  return {
    error: errorCheckout,
    errorCode: errorCodeCheckout as ErrorCode | TopazError | 'Hard_ID_Max_Failures',
  }
}

export const cardScanRequiredErrors: ErrorCode[] = [ErrorCode.CardScanRequired]
export const isOverduePaymentEligibleErrorCode = (error: ErrorCode): boolean => error === ErrorCode.OverduePaymentsCheckoutPaymentEligible
export const isCardScanRequiredErrorCode = (error: ErrorCode): boolean => cardScanRequiredErrors.includes(error)

export const PERSONALIZATION_REDIRECTION_DELAY_MS: number = app.PERSONALIZATION_REDIRECTION_DELAY_MS ?? 10000
export const PERSONALIZATION_POSITIVE_ACTIONED_REDIRECTION_DELAY_MS: number = app.PERSONALIZATION_POSITIVE_ACTIONED_REDIRECTION_DELAY_MS ?? 2000
export const PERSONALIZATION_NEGATIVE_ACTIONED_REDIRECTION_DELAY_MS: number = app.PERSONALIZATION_NEGATIVE_ACTIONED_REDIRECTION_DELAY_MS ?? 0
export const PERSONALIZATION_POST_CAPTURE_TIMEOUT: number = app.PERSONALIZATION_POST_CAPTURE_TIMEOUT ?? 8000
export const BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL: number = app.BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL ?? 5000
export const BIOMETRIC_VERIFYING_DEFAULT_WAIT_TIME: number = app.BIOMETRIC_VERIFYING_DEFAULT_WAIT_TIME ?? 90000
export const BIOMETRIC_VERIFYING_TIME_OUT: number = app.BIOMETRIC_VERIFYING_TIME_OUT ?? 300000
export const PERSONA_ENV_ID = app.PERSONA_ENV_ID
export const PERSONA_TEMPLATE_ID = app.PERSONA_TEMPLATE_ID

export enum CheckoutManagedUserAgent {
  AfterpayMobile = 'AfterpayMobile',
  CashAppMobile = 'com.squareup.cash',
}

const checkoutConfirm2FARequiredErrorCodes: ErrorCode[] = [ErrorCode.CheckoutConfirm2FARequired, ErrorCode.CheckoutConfirm2FARequiredNoEmail]
const checkoutConfirmEFARequiredErrorCodes: ErrorCode[] = [ErrorCode.ExtendedFraudAlert]
export const isCheckoutConfirm2FARequired = (errorCode: ErrorCode): boolean => checkoutConfirm2FARequiredErrorCodes.includes(errorCode)
export const isCheckoutConfirmEFARequired = (errorCode: ErrorCode): boolean => checkoutConfirmEFARequiredErrorCodes.includes(errorCode)

export enum IntegrationType {
  APPLEPAY = 'applepay',
}
export const APPLE_ORDER_SOURCES = [OrderSource.APPLE_PAYLATER_ONLINE, OrderSource.APPLE_PAYLATER]

export const isGenericApiError = (error: unknown): error is GenericApiError =>
  typeof error !== 'undefined' &&
  typeof error === 'object' &&
  error !== null &&
  ('responseHeaders' in error || 'requestUrl' in error || 'status' in error || 'stack' in error)

export const CLICKS_TO_REVEAL_SESSION_ID = 5

export interface EnrichedItem extends Item {
  childId?: string
  apr?: string
  interestAmount?: Money
  numberOfInstallments?: number
  paymentSchedules?: CheckoutPaymentSchedule[]
  amountDueToday?: Money
  paymentType?: PaymentType
  monthlyAmount?: Money
  downPaymentAmount?: Money
}

export interface DataVault<Entity> {
  ids: string[]
  data: Record<string, Entity>
}
